<template>
    <section class="section">
        <div class="container-fluid">
            <div v-if="!bracket.parameter_id && requestsInProgress > 0" class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <div v-else-if="bracket.parameter_id">
                <div class="column is-12">
                    <h2 class="is-size-2">
                        {{ bracket.name }}
                    </h2>
                    <p class="mb-4">{{ bracketType }}</p>

                    <div class="buttons">
                        <a v-if="canBeDownloaded" class="button" :href="eventAdmin().downloadBracketPage(eventId, bracket.parameter_id)">
                            <span class="icon">
                                <i class="fas fa-download" />
                            </span>
                            <span>
                                {{ translate("common.download") }}
                            </span>
                        </a>
                        <button v-else class="button" disabled>
                            <span class="icon">
                                <i class="fas fa-download" />
                            </span>
                            <span>{{ translate("common.download") }}</span>
                        </button>

                        <bracket-color-options @clearColoring="onClearColoring" @coloringChoice="onColoringChoice" />

                        <a :href="eventAdmin().bracketsListingPage(eventId)" class="button">
                            <span class="icon">
                                <i class="fas fa-arrow-left" />
                            </span>
                            <span>
                                {{ translate("common.go_back_to_list") }}
                            </span>
                        </a>

                        <button @click="confirmDeleteBracket" class="button is-danger">
                            <span class="icon">
                                <i class="fas fa-trash" />
                            </span>
                            <span>{{ translate("common.delete") }}</span>
                        </button>
                    </div>

                    <div class="mb-1" v-if="!addMissingMode && !editMode">
                        <button class="button is-small" @click="enableEditMode">
                            <span class="icon">
                                <i class="fas fa-edit" />
                            </span>
                            <span>
                                {{ translate("admin_show_bracket.edit_mode") }}
                            </span>
                        </button>
                    </div>

                    <div>{{ translate("admin_show_bracket.size") }} {{ bracket.competitors.length }}</div>

                    <div v-if="bracket.status_details.missing_competitors && !addMissingMode">
                        <span class="has-text-danger">
                            <span class="has-text-weight-bold">{{ translate("admin_show_bracket.missing_competitors") }}</span>
                            {{ bracket.status_details.missing_competitors }}
                        </span>
                        <a class="ml-3 has-text-success" v-if="!editMode" @click="openAddMissingCompetitorModal">
                            <span class="icon">
                                <i class="fas fa-user-plus" />
                            </span>
                            <span>{{ translate("admin_show_bracket.click_to_add_missing") }}</span>
                        </a>
                    </div>

                    <div v-if="bracket.competitors.length" class="mt-3 mb-3">
                        <div class="buttons" v-if="editMode">
                            <LoadingButton
                                :is-loading="savingBracket"
                                class="button is-primary is-small"
                                @click="saveBracket"
                                :disabled="disabledEditModeButtons"
                            >
                                {{ translate("common.save") }}
                            </LoadingButton>
                            <button class="button is-small" @click="fetchBracket" :disabled="disabledEditModeButtons">
                                {{ translate("common.cancel") }}
                            </button>
                        </div>
                        <div v-if="addMissingMode">
                            <button class="button is-small is-danger" @click="cancelAddingCompetitor">
                                <span class="icon">
                                    <i class="fas fa-times" />
                                </span>
                                <span>{{ translate("admin_show_bracket.cancel_adding") }}</span>
                            </button>
                        </div>
                    </div>

                    <div>
                        <table-bracket
                            ref="tableBracket"
                            :bracket-competitors="bracket.competitors"
                            :edit-mode="editMode"
                            :add-missing-mode="addMissingMode"
                            :removed-competitors="bracket.removed_competitors"
                            :disqualified-competitors="bracket.disqualifiedCompetitors"
                            @addMissingCompetitorToIndex="onAddedCompetitorUnderIndex"
                        />
                    </div>
                </div>
            </div>
        </div>

        <AddMissingCompetitorModal
            ref="addMissingCompetitorModal"
            :event-id="eventId"
            @missingCompetitorChosen="onChosenMissingCompetitor"
        />
    </section>
</template>

<script>
    import requestMixin from "../Common/requestMixin";
    import TableBracket from "./TableBracket.vue";
    import BracketColorOptions from "./BracketColorOptions.vue";
    import constantsMixin from "../Common/constantsMixin";
    import translatorMixin from "../Common/translatorMixin";
    import urlsMixin from "../Urls/urlsMixin";
    import apiMixin from "../Api/apiMixin";
    import errorHandlerMixin from "../Common/errorHandlerMixin";
    import toastMixin from "../Common/toastMixin";
    import AddMissingCompetitorModal from "./AddMissingCompetitorModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";

    export default {
        name: "show-bracket-app",
        components: {
            LoadingButton,
            LoadingSpinner2,
            AddMissingCompetitorModal,
            "table-bracket": TableBracket,
            "bracket-color-options": BracketColorOptions,
        },
        mixins: [requestMixin, constantsMixin, translatorMixin, urlsMixin, apiMixin, errorHandlerMixin, toastMixin],
        props: ["bracketParameterId", "eventId"],
        data() {
            return {
                editMode: false,
                bracket: {
                    competitors: [],
                    status_details: {},
                    removed_competitors: [],
                },
                missingCompetitorToAdd: 0,
                savingBracket: false,
                originalEditOrder: [],
            };
        },
        computed: {
            addMissingMode() {
                return !!this.missingCompetitorToAdd;
            },
            canBeDownloaded() {
                return [1, 2, 3].indexOf(this.bracket.type) > -1;
            },
            disabledEditModeButtons() {
                return !!this.requestsInProgress;
            },
            bracketType() {
                if (this.bracket.type) {
                    const translationKey = this.getConstant("BRACKET_TYPE_TRANSLATIONS")[this.bracket.type];
                    return this.translate(translationKey);
                } else {
                    return "";
                }
            },
        },
        mounted() {
            return this.fetchBracket();
        },
        methods: {
            fetchBracket() {
                return this.bracketsApi()
                    .getBracket(this.eventId, this.bracketParameterId)
                    .then(bracket => {
                        this.bracket = bracket;
                        this.editMode = false;
                        this.missingCompetitorToAdd = null;
                    })
                    .catch(err => {
                        if (err.statusCode === 404) {
                            window.location.href = this.eventAdmin().bracketsListingPage(this.eventId);
                            return;
                        }
                        this.handleError(err);
                    });
            },
            saveBracket() {
                const competitors = this.$refs.tableBracket.getCompetitors();
                this.savingBracket = true;
                this.bracket.competitors = competitors;

                const currentIds = competitors.map(competitor => competitor.id);
                const orderChanged = JSON.stringify(this.originalEditOrder) !== JSON.stringify(currentIds);

                return this.bracketsApi()
                    .persistBracket({
                        eventId: this.eventId,
                        parameterId: this.bracket.parameter_id,
                        type: this.bracket.type,
                        name: this.bracket.name,
                        competitors: currentIds,
                        seedingType: this.bracket.seeding_type,
                        manualChange: this.bracket.manual_change || orderChanged,
                    })
                    .then(() => {
                        this.successToast(this.translate("admin_create_bracket.bracket_save"));
                        this.editMode = false;
                    })
                    .catch(this.handleError)
                    .finally(() => {
                        this.savingBracket = false;
                    });
            },
            assertValue(condition, errorMessage, throwError) {
                if (condition) {
                    return true;
                }
                alert(errorMessage);
                throwError = throwError === undefined ? true : !!throwError;
                if (throwError) {
                    throw Error("Cannot save/generate bracket.");
                }
                return false;
            },
            onClearColoring() {
                this.$refs.tableBracket.clearColoring();
            },
            onColoringChoice(coloring) {
                this.$refs.tableBracket.setRowsColoring(coloring);
            },
            confirmDeleteBracket() {
                if (!confirm(this.translate("common.are_you_sure"))) {
                    return;
                }

                return this.bracketsApi()
                    .deleteBracket(this.eventId, this.bracket.parameter_id)
                    .then(() => {
                        window.location.href = this.eventAdmin().bracketsListingPage(this.eventId);
                    })
                    .catch(this.handleError);
            },
            openAddMissingCompetitorModal() {
                this.$refs.addMissingCompetitorModal.open(this.bracketParameterId);
            },
            onChosenMissingCompetitor(eventRegistration) {
                this.missingCompetitorToAdd = eventRegistration;
            },
            onAddedCompetitorUnderIndex(index) {
                if (!confirm(this.translate("common.are_you_sure"))) {
                    return;
                }

                return this.bracketsApi()
                    .replaceCompetitorInBracket(this.eventId, this.bracketParameterId, this.missingCompetitorToAdd.id, index)
                    .then(() => {
                        this.missingCompetitorToAdd = null;
                        this.successToast(this.translate("admin_show_bracket.competitor_added"));

                        return this.fetchBracket();
                    })
                    .catch(this.handleError);
            },
            cancelAddingCompetitor() {
                this.missingCompetitorToAdd = null;
            },
            enableEditMode() {
                this.$refs.tableBracket.competitors = this.bracket.competitors;
                this.originalEditOrder = this.bracket.competitors.map(c => c.id);
                this.editMode = true;
            },
        },
    };
</script>

<style scoped></style>
